import { mapValues } from 'lodash';

/**
 * The result of a validation. Currently this is only the status but future
 * enhancements may require more information for a validation result so it has
 * been made an object to promote backwards compatibilty when new features added.
 */
export type ValidationResult = {
   isValid: boolean;
 }

/**
 * A function which takes in a value and any necessary context to validate the value.
 * @returns True if the value is valid, false otherwise.
 */
export type SingleValidator = (value: any, context: ?any) => ValidationResult;

export type ValidationsResult = { [key: string]: boolean };

/**
 * A function which takes in a value and any necessary context to validate the value.
 * @returns The results of several validations with the key indicating the validation.
 */
export type MultiValidator = (value: any, context: ?any) => ValidationsResult;

/**
 * A single validation with the provided validator.
 */
export type Validation = {
  validator: SingleValidator
};

/**
 * A set of validations to run for a given value.
 */
export type Validations = { [key: string]: Validation };

/**
 * Validates the value against the provided validations.
 * @param validations - Either a set of validations or a function to validate the value.
 * @param context - (optional) Any external data that is required by a validation.
 *    This will be provided to every validation after the value.
 * @returns - The results of each validation.
 */
export const validate = (validations: Validations | MultiValidator, value, context): ValidationsResult => {
  if (!validations) {
    return {};
  }
  if (typeof validations === 'function') {
    return validations(value);
  }
  return mapValues(validations, (validation) => {
    const { validator } = validation;
    return {
      // Using !! to force value to boolean.
      isValid: !!validator(value, context)
    };
  });
};
